/**
 * A patient's intake profile ("ficha"): who they are, as the therapist learns
 * it in the first sessions. The session analysis proposes values as
 * suggestions; only the therapist puts them on the profile.
 */

export type IntakeFieldKey =
    | "age"
    | "family_nucleus"
    | "children"
    | "occupation"
    | "lives_with"
    | "core_issue"
    | "request";

export type IntakeFieldKind = "number" | "short_text" | "long_text";

/** The longest text the server accepts, and the highest age and number of children. */
export const INTAKE_TEXT_MAX = 500;
export const INTAKE_AGE_MAX = 120;
export const INTAKE_CHILDREN_MAX = 20;

/**
 * The fields in the order the profile shows them. A number field carries its
 * highest value and what a value out of range says.
 */
export const INTAKE_FIELDS: {
    key: IntakeFieldKey;
    kind: IntakeFieldKind;
    labelKey: string;
    max?: number;
    invalidKey?: string;
}[] = [
    { key: "age", kind: "number", labelKey: "patient.intake.fields.age", max: INTAKE_AGE_MAX, invalidKey: "patient.intake.errors.ageInvalid" },
    { key: "occupation", kind: "short_text", labelKey: "patient.intake.fields.occupation" },
    { key: "lives_with", kind: "short_text", labelKey: "patient.intake.fields.livesWith" },
    {
        key: "children",
        kind: "number",
        labelKey: "patient.intake.fields.children",
        max: INTAKE_CHILDREN_MAX,
        invalidKey: "patient.intake.errors.childrenInvalid",
    },
    { key: "family_nucleus", kind: "long_text", labelKey: "patient.intake.fields.familyNucleus" },
    { key: "core_issue", kind: "long_text", labelKey: "patient.intake.fields.coreIssue" },
    { key: "request", kind: "long_text", labelKey: "patient.intake.fields.request" },
];

export interface IntakeValue {
    value: string;
    /** `ai` when the therapist accepted it from a suggestion. */
    source: "therapist" | "ai";
    source_session_id: string | null;
    updated_at: string;
}

export interface IntakeSuggestion {
    id: string;
    field: IntakeFieldKey;
    value: string;
    /** The excerpt of the session notes the value was read from. */
    evidence: string;
    session_id: string;
    session_date: string | null;
    created_at: string;
}

export interface IntakeProfile {
    /** A field with no value is absent. */
    values: Partial<Record<IntakeFieldKey, IntakeValue>>;
    suggestions: IntakeSuggestion[];
    /** False while suggestions are withheld; the reason says why. */
    suggestions_available: boolean;
    /** Memoria aumentada not adopted, or the patient's AI consent not active. */
    suggestions_unavailable_reason?: "tool_not_adopted" | "no_consent" | null;
}

/** The profile as a centre's administrator reads it: the values, never the suggestions. */
export interface CenterIntakeProfile {
    values: IntakeProfile["values"];
}

/** A partial save: only the fields present change; `null` or `""` clears one. */
export type IntakeProfileInput = Partial<Record<IntakeFieldKey, string | null>>;

export function intakeField(key: string) {
    return INTAKE_FIELDS.find((field) => field.key === key);
}

/**
 * What a form can tell about one value before asking the server: an i18n
 * key, or null when it may be sent. An empty value clears the field.
 */
export function validateIntakeValue(key: IntakeFieldKey, value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const field = intakeField(key);
    if (field?.kind === "number") {
        const count = Number(trimmed);
        const inRange = Number.isInteger(count) && count >= 0 && count <= (field.max ?? Infinity);
        return inRange ? null : (field.invalidKey ?? "patient.intake.errors.valueInvalid");
    }
    return trimmed.length > INTAKE_TEXT_MAX ? "patient.intake.errors.valueTooLong" : null;
}
